/**
 * Commission Service
 *
 * API client for the staff-referrer commission system (configs,
 * statements, and offline payout batches). Referrers are identified by
 * `user_id` — any clinic user (practitioner,
 * clinic_admin, admin, super_admin) can be attributed as a referrer.
 */

import api from './api'

// ─── Types ───────────────────────────────────────────────────────────────

export type ReferrerRole =
  | 'practitioner'
 
  | 'clinic_admin'
  | 'admin'
  | 'super_admin'
  | string

export interface Referrer {
  user_id: string
  name: string
  email: string
  primary_role: ReferrerRole
  roles: string[]
  credentials?: string | null
  effective_rate_bps?: number | null
}

export interface CommissionConfig {
  id: string
  clinic_id: string
  referrer_user_id: string
  role_snapshot: ReferrerRole | null
  commission_rate_bps: number
  effective_from: string
  effective_to: string | null
  is_active: boolean
  created_by: string | null
  created_at: string
  updated_at: string
  referrer?: {
    user_id: string
    name: string
    email: string
    primary_role: ReferrerRole
  } | null
  ratePercentage?: number
}

export interface CommissionStatementItem {
  id: string
  statement_id: string
  appointment_id: string | null
  guided_order_id: string | null
  transaction_id: string | null
  patient_id: string | null
  clinic_revenue_cents: number
  commission_rate_bps: number
  commission_amount_cents: number
  service_date: string | null
  patient?: {
    id: string
    first_name: string
    middle_name?: string | null
    last_name: string
  }
}

export type CommissionStatementStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'exported'
  | 'voided'

export interface CommissionStatement {
  id: string
  clinic_id: string
  referrer_user_id: string
  role_snapshot: ReferrerRole | null
  period_start: string
  period_end: string
  status: CommissionStatementStatus
  gross_clinic_revenue_cents: number
  eligible_revenue_cents: number
  commission_rate_bps: number
  commission_amount_cents: number
  referral_count: number
  submitted_at: string | null
  approved_at: string | null
  approved_by: string | null
  exported_at: string | null
  voided_at: string | null
  void_reason: string | null
  notes: string | null
  payout_batch_id: string | null
  created_at: string
  updated_at: string
  items?: CommissionStatementItem[]
  referrer?: Referrer | null
  referrerUser?: {
    id: string
    first_name: string
    last_name: string
    email: string
    role: string
  }
  commissionDollars?: string
  revenueDollars?: string
}

export type PayoutBatchStatus =
  | 'draft'
  | 'approved'
  | 'exported'
  | 'settled'
  | 'voided'

export interface PayoutBatch {
  id: string
  clinic_id: string
  referrer_user_id: string
  role_snapshot: ReferrerRole | null
  status: PayoutBatchStatus
  total_amount_cents: number
  statement_count: number
  period_start: string | null
  period_end: string | null
  external_reference: string | null
  settlement_method: string | null
  notes: string | null
  approved_by: string | null
  approved_at: string | null
  exported_at: string | null
  settled_by: string | null
  settled_at: string | null
  voided_by: string | null
  voided_at: string | null
  void_reason: string | null
  created_at: string
  updated_at: string
  referrer?: Referrer | null
  referrerUser?: {
    id: string
    first_name: string
    last_name: string
    email: string
    role: string
  }
  statements?: CommissionStatement[]
  totalDollars?: string
}

export interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────

export const bpsToPercentage = (bps: number): number => bps / 100
export const percentageToBps = (pct: number): number => Math.round(pct * 100)
export const centsToDollars = (cents: number): string =>
  (cents / 100).toFixed(2)

// ─── Clinic default rate ─────────────────────────────────────────────────

export interface ClinicDefaultRate {
  rateBps: number
  ratePercentage: number
}

/**
 * Get the clinic-level default referral rate. Applied to any referrer
 * without a per-user override.
 */
export const getClinicDefaultRate = async (
  clinicId: string,
): Promise<ClinicDefaultRate> => {
  const response = await api.get(
    `/clinics/${clinicId}/commissions/default-rate`,
  )
  return response.data.data
}

export const setClinicDefaultRate = async (
  clinicId: string,
  ratePercentage: number,
): Promise<ClinicDefaultRate> => {
  const response = await api.put(
    `/clinics/${clinicId}/commissions/default-rate`,
    { ratePercentage },
  )
  return response.data.data
}

// ─── Referrer directory ──────────────────────────────────────────────────

/**
 * Unified list of clinic users eligible as referrers.
 */
export const getReferrers = async (clinicId: string): Promise<Referrer[]> => {
  const response = await api.get(`/clinics/${clinicId}/commissions/referrers`)
  return response.data.data
}

export const getEffectiveRate = async (
  clinicId: string,
  referrerUserId: string,
  date?: string,
): Promise<{ rateBps: number; ratePercentage: number }> => {
  const params = date ? { date } : {}
  const response = await api.get(
    `/clinics/${clinicId}/commissions/referrers/${referrerUserId}/rate`,
    { params },
  )
  return response.data.data
}

// ─── Configs ─────────────────────────────────────────────────────────────

export const listConfigs = async (
  clinicId: string,
  options?: { includeInactive?: boolean; referrerUserId?: string },
): Promise<CommissionConfig[]> => {
  const params = {
    includeInactive: options?.includeInactive ? 'true' : undefined,
    referrerUserId: options?.referrerUserId,
  }
  const response = await api.get(`/clinics/${clinicId}/commissions/configs`, {
    params,
  })
  return response.data.data
}

export const createConfig = async (
  clinicId: string,
  data: {
    referrerUserId: string
    ratePercentage: number
    effectiveFrom: string
    effectiveTo?: string
  },
): Promise<CommissionConfig> => {
  const response = await api.post(
    `/clinics/${clinicId}/commissions/configs`,
    data,
  )
  return response.data.data
}

export const updateConfig = async (
  clinicId: string,
  configId: string,
  data: {
    ratePercentage?: number
    effectiveTo?: string
    isActive?: boolean
  },
): Promise<CommissionConfig> => {
  const response = await api.put(
    `/clinics/${clinicId}/commissions/configs/${configId}`,
    data,
  )
  return response.data.data
}

export const deactivateConfig = async (
  clinicId: string,
  configId: string,
): Promise<CommissionConfig> => {
  const response = await api.post(
    `/clinics/${clinicId}/commissions/configs/${configId}/deactivate`,
  )
  return response.data.data
}

// ─── Statements ──────────────────────────────────────────────────────────

export const generateStatements = async (
  clinicId: string,
  periodStart: string,
  periodEnd: string,
): Promise<{ generated: number; skipped: number; errors: number }> => {
  const response = await api.post(
    `/clinics/${clinicId}/commissions/statements/generate`,
    { periodStart, periodEnd },
  )
  return response.data.data
}

export const listStatements = async (
  clinicId: string,
  options?: {
    page?: number
    limit?: number
    status?: string
    referrerUserId?: string
    periodStart?: string
    periodEnd?: string
    unpaidOnly?: boolean
  },
): Promise<PaginatedResponse<CommissionStatement>> => {
  const response = await api.get(
    `/clinics/${clinicId}/commissions/statements`,
    { params: options },
  )
  return response.data
}

export const getStatement = async (
  clinicId: string,
  statementId: string,
): Promise<CommissionStatement> => {
  const response = await api.get(
    `/clinics/${clinicId}/commissions/statements/${statementId}`,
  )
  return response.data.data
}

export const submitStatement = async (
  clinicId: string,
  statementId: string,
): Promise<CommissionStatement> => {
  const response = await api.post(
    `/clinics/${clinicId}/commissions/statements/${statementId}/submit`,
  )
  return response.data.data
}

export const approveStatement = async (
  clinicId: string,
  statementId: string,
): Promise<CommissionStatement> => {
  const response = await api.post(
    `/clinics/${clinicId}/commissions/statements/${statementId}/approve`,
  )
  return response.data.data
}

export const voidStatement = async (
  clinicId: string,
  statementId: string,
  reason: string,
): Promise<CommissionStatement> => {
  const response = await api.post(
    `/clinics/${clinicId}/commissions/statements/${statementId}/void`,
    { reason },
  )
  return response.data.data
}

export const exportStatement = async (
  clinicId: string,
  statementId: string,
): Promise<Blob> => {
  const response = await api.get(
    `/clinics/${clinicId}/commissions/statements/${statementId}/export`,
    { responseType: 'blob' },
  )
  return response.data
}

// ─── Payout batches ──────────────────────────────────────────────────────

export const listPayouts = async (
  clinicId: string,
  options?: {
    status?: PayoutBatchStatus
    referrerUserId?: string
    periodStart?: string
    periodEnd?: string
    limit?: number
    offset?: number
  },
): Promise<PaginatedResponse<PayoutBatch>> => {
  const response = await api.get(`/clinics/${clinicId}/commissions/payouts`, {
    params: options,
  })
  return response.data
}

export const getPayout = async (
  clinicId: string,
  batchId: string,
): Promise<PayoutBatch> => {
  const response = await api.get(
    `/clinics/${clinicId}/commissions/payouts/${batchId}`,
  )
  return response.data.data
}

export const createPayout = async (
  clinicId: string,
  data: { referrerUserId: string; statementIds: string[]; notes?: string },
): Promise<PayoutBatch> => {
  const response = await api.post(
    `/clinics/${clinicId}/commissions/payouts`,
    data,
  )
  return response.data.data
}

export const approvePayout = async (
  clinicId: string,
  batchId: string,
): Promise<PayoutBatch> => {
  const response = await api.post(
    `/clinics/${clinicId}/commissions/payouts/${batchId}/approve`,
  )
  return response.data.data
}

export const settlePayout = async (
  clinicId: string,
  batchId: string,
  data: { reference?: string; method?: string; notes?: string },
): Promise<PayoutBatch> => {
  const response = await api.post(
    `/clinics/${clinicId}/commissions/payouts/${batchId}/settle`,
    data,
  )
  return response.data.data
}

export const voidPayout = async (
  clinicId: string,
  batchId: string,
  reason: string,
): Promise<PayoutBatch> => {
  const response = await api.post(
    `/clinics/${clinicId}/commissions/payouts/${batchId}/void`,
    { reason },
  )
  return response.data.data
}

export const exportPayout = async (
  clinicId: string,
  batchId: string,
): Promise<Blob> => {
  const response = await api.get(
    `/clinics/${clinicId}/commissions/payouts/${batchId}/export`,
    { responseType: 'blob' },
  )
  return response.data
}

export const listEligibleStatements = async (
  clinicId: string,
  referrerUserId?: string,
): Promise<CommissionStatement[]> => {
  const response = await api.get(
    `/clinics/${clinicId}/commissions/payouts/eligible-statements`,
    { params: referrerUserId ? { referrerUserId } : {} },
  )
  return response.data.data
}

export default {
  getClinicDefaultRate,
  setClinicDefaultRate,
  getReferrers,
  getEffectiveRate,
  listConfigs,
  createConfig,
  updateConfig,
  deactivateConfig,
  generateStatements,
  listStatements,
  getStatement,
  submitStatement,
  approveStatement,
  voidStatement,
  exportStatement,
  listPayouts,
  getPayout,
  createPayout,
  approvePayout,
  settlePayout,
  voidPayout,
  exportPayout,
  listEligibleStatements,
  bpsToPercentage,
  percentageToBps,
  centsToDollars,
}
